import { useCallback, useRef, useState } from 'react';

/** A new request id (GUID) for one user action. */
export function newRequestId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // crypto.randomUUID needs a secure context; fall back to an RFC 4122 v4 id.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.floor(Math.random() * 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

export interface SingleFlight {
    /** True from the first click until the request settles. Drives the disabled state and spinner. */
    inFlight: boolean;
    /**
     * Runs `action` unless one is already running; a second call while one runs returns
     * undefined and does nothing. The check is a ref set synchronously, so a double click, a
     * repeated Enter or a second submit in the same tick is dropped before React re-renders the
     * disabled button.
     *
     * `action` gets the request id for this user action. Send it with a copy request: the server
     * returns the same case for a repeated id. The id stays the same when the action is retried
     * after a failure (the first attempt may have made the case before the error reached the
     * client) and changes after a success or `resetRequestId()`.
     */
    run: <T>(action: (requestId: string) => Promise<T>) => Promise<T | undefined>;
    /** Starts a new user action: call it when a dialog opens or its target changes. */
    resetRequestId: () => void;
}

/**
 * Blocks re-entry into an async action from the first click until it settles, and keeps one
 * request id per user action. Use it for every action that creates a case from an existing one.
 * See docs/AUTOSAVE_DETAILS.md "Copy idempotency".
 */
export function useSingleFlight(): SingleFlight {
    const inFlightRef = useRef(false);
    const requestIdRef = useRef<string | null>(null);
    const [inFlight, setInFlight] = useState(false);

    const run = useCallback(async <T,>(action: (requestId: string) => Promise<T>): Promise<T | undefined> => {
        if (inFlightRef.current) return undefined;
        inFlightRef.current = true;
        setInFlight(true);
        const requestId = requestIdRef.current ?? newRequestId();
        requestIdRef.current = requestId;
        try {
            const result = await action(requestId);
            // Done: the next click is a new user action.
            if (requestIdRef.current === requestId) requestIdRef.current = null;
            return result;
        } finally {
            inFlightRef.current = false;
            setInFlight(false);
        }
    }, []);

    const resetRequestId = useCallback(() => {
        requestIdRef.current = null;
    }, []);

    return { inFlight, run, resetRequestId };
}
